import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Clock,
    Calculator,
    Car,
    Repeat2,
    Calendar,
    TrendingUp,
    Smartphone,
    ShieldCheck,
    Users,
    CheckSquare,
    BarChart3,
    Settings,
    Sparkles,
    Plug,
    Lock,
    Server,
    HelpCircle,
    Receipt,
    AlertCircle,
    Globe,
    Briefcase,
    Layers,
    Bell,
    UserCheck,
    Eye,
    FileText,
    Download,
    Wifi,
    Monitor,
    Star,
    ArrowLeft,
} from 'lucide-react';
import BrowserChrome from '../../components/public/BrowserChrome';
import PublicFooter from '../../components/public/PublicFooter';

const LOGO_URL = `${process.env.PUBLIC_URL}/hisland-logo.png`;

// ── Topic definitions ──────────────────────────────────────────────────────
// Each topic has: key, Icon, visual (type + props), features[]
// visual types: 'screenshot', 'video', 'screenshot-pair', 'screenshot+video', null

const TOPICS = [
    {
        key: 'time_expenses',
        Icon: Clock,
        visual: { type: 'video', src: 'time-reporting', urlPath: '/dashboard/log-reports' },
        features: [
            { key: 'calendar_view', Icon: Calendar },
            { key: 'mileage', Icon: Car },
            { key: 'receipts', Icon: Receipt },
            { key: 'allowance', Icon: FileText },
            { key: 'leave', Icon: Calendar },
            { key: 'flex', Icon: Repeat2 },
        ],
    },
    {
        key: 'approvals',
        Icon: CheckSquare,
        visual: { type: 'video', src: 'admin-approvals', urlPath: '/dashboard/approvals' },
        features: [
            { key: 'separate_flows', Icon: Layers },
            { key: 'calendar_lock', Icon: Lock },
            { key: 'edit_request', Icon: AlertCircle },
            { key: 'project_membership', Icon: Briefcase },
            { key: 'manager_routing', Icon: UserCheck },
        ],
    },
    {
        key: 'payroll',
        Icon: Calculator,
        visual: { type: 'screenshot', src: 'analytics-dashboard', urlPath: '/dashboard/analytics' },
        features: [
            { key: 'monthly_report', Icon: FileText },
            { key: 'employer_tax', Icon: Calculator },
            { key: 'billing_basis', Icon: Receipt },
            { key: 'payslip', Icon: Lock },
            { key: 'margin', Icon: TrendingUp },
            { key: 'analytics', Icon: BarChart3 },
            { key: 'base_hours', Icon: Clock },
        ],
    },
    {
        key: 'admin_users',
        Icon: Users,
        visual: { type: 'screenshot', src: 'team-dashboard', urlPath: '/dashboard' },
        features: [
            { key: 'team_dashboard', Icon: Users },
            { key: 'roles', Icon: UserCheck },
            { key: 'projects', Icon: Briefcase },
            { key: 'budget_alerts', Icon: Bell },
            { key: 'onboarding', Icon: Star },
        ],
    },
    {
        key: 'experience',
        Icon: Sparkles,
        visual: {
            type: 'screenshot-pair',
            a: { src: 'mobile-timesheet', urlPath: '/dashboard/log-reports', label: 'mobile' },
            b: { src: 'unified-day-dialog', urlPath: '/dashboard/log-reports', label: 'dialog' },
        },
        features: [
            { key: 'unified_dialog', Icon: Layers },
            { key: 'calendar_pills', Icon: Calendar },
            { key: 'public_holidays', Icon: AlertCircle },
            { key: 'receipt_inline', Icon: Receipt },
            { key: 'pwa', Icon: Smartphone },
            { key: 'quick_entry', Icon: Clock },
            { key: 'responsive', Icon: Monitor },
        ],
    },
    {
        key: 'integrations',
        Icon: Plug,
        visual: { type: 'screenshot', src: 'fortnox-integration', urlPath: '/dashboard/integrations' },
        features: [
            { key: 'fortnox', Icon: Plug },
            { key: 'ms365', Icon: Globe },
            { key: 'brevo', Icon: Bell },
            { key: 'skatteverket', Icon: FileText },
            { key: 'riksbanken', Icon: TrendingUp },
        ],
    },
    {
        key: 'security',
        Icon: ShieldCheck,
        visual: null,
        features: [
            { key: 'multi_tenant', Icon: Layers },
            { key: 'audit_log', Icon: Eye },
            { key: 'login_block', Icon: Lock },
            { key: 'data_deletion', Icon: AlertCircle },
        ],
    },
    {
        key: 'infrastructure',
        Icon: Server,
        visual: null,
        features: [
            { key: 'hosting', Icon: Server },
            { key: 'eu_backups', Icon: Download },
            { key: 'daily_backups', Icon: Lock },
            { key: 'retention', Icon: FileText },
            { key: 'snapshots', Icon: Layers },
            { key: 'integrity', Icon: CheckSquare },
            { key: 'rpo_rto', Icon: Clock },
            { key: 'tls', Icon: ShieldCheck },
            { key: 'uptime', Icon: Bell },
            { key: 'security_updates', Icon: AlertCircle },
            { key: 'firewall', Icon: Lock },
            { key: 'no_tracking', Icon: Eye },
        ],
    },
    {
        key: 'support',
        Icon: HelpCircle,
        visual: {
            type: 'screenshot+video',
            screenshot: { src: 'help-panel-open', urlPath: '/dashboard/log-reports' },
            video: { src: 'expense-mileage', urlPath: '/dashboard/log-reports' },
        },
        features: [
            { key: 'feedback', Icon: AlertCircle },
            { key: 'contextual_help', Icon: HelpCircle },
            { key: 'built_and_maintained', Icon: Settings },
            { key: 'bilingual', Icon: Globe },
            { key: 'data_ownership', Icon: Download },
        ],
    },
];

// ── Screenshot helper ──────────────────────────────────────────────────────

function ScreenshotBlock({ src, urlPath, alt, className = '' }) {
    return (
        <BrowserChrome urlPath={urlPath} className={className}>
            <img
                src={`${process.env.PUBLIC_URL}/screenshots/${src}.png`}
                alt={alt || src}
                className="w-full h-auto block"
                loading="lazy"
                decoding="async"
            />
        </BrowserChrome>
    );
}

// ── Video helper ───────────────────────────────────────────────────────────

function VideoBlock({ src, urlPath, className = '' }) {
    const { t } = useTranslation();
    // Prefer MP4; fall back to WebM
    const mp4Src = `${process.env.PUBLIC_URL}/videos/${src}.mp4`;
    const webmSrc = `${process.env.PUBLIC_URL}/videos/${src}.webm`;

    return (
        <BrowserChrome urlPath={urlPath} className={className}>
            <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-auto block"
                aria-label={t('features.video_demo_label')}
            >
                <source src={mp4Src} type="video/mp4" />
                <source src={webmSrc} type="video/webm" />
            </video>
        </BrowserChrome>
    );
}

// ── Topic visual renderer ──────────────────────────────────────────────────

function TopicVisual({ visual, topicKey, lang }) {
    if (!visual) return null;

    if (visual.type === 'screenshot') {
        return (
            <div className="mb-10">
                <ScreenshotBlock src={visual.src} urlPath={visual.urlPath} alt={topicKey} />
            </div>
        );
    }

    if (visual.type === 'video') {
        return (
            <div className="mb-10">
                <VideoBlock src={visual.src} urlPath={visual.urlPath} />
            </div>
        );
    }

    if (visual.type === 'screenshot-pair') {
        return (
            <div className="mb-10 grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                <ScreenshotBlock src={visual.a.src} urlPath={visual.a.urlPath} alt={visual.a.label} />
                <ScreenshotBlock src={visual.b.src} urlPath={visual.b.urlPath} alt={visual.b.label} />
            </div>
        );
    }

    if (visual.type === 'screenshot+video') {
        return (
            <div className="mb-10 grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
                <ScreenshotBlock
                    src={visual.screenshot.src}
                    urlPath={visual.screenshot.urlPath}
                    alt="help"
                />
                <VideoBlock src={visual.video.src} urlPath={visual.video.urlPath} />
            </div>
        );
    }

    return null;
}

// ── Feature card ───────────────────────────────────────────────────────────

function FeatureCard({ topicKey, featureKey, Icon }) {
    const { t } = useTranslation();
    return (
        <div className="border border-border rounded-lg p-4 bg-card hover:border-foreground/20 transition-colors">
            <Icon className="size-4 text-muted-foreground mb-3" aria-hidden="true" />
            <p className="text-sm font-semibold leading-snug mb-1">
                {t(`features.topics.${topicKey}.features.${featureKey}.title`)}
            </p>
            <p className="text-xs text-muted-foreground leading-snug">
                {t(`features.topics.${topicKey}.features.${featureKey}.desc`)}
            </p>
        </div>
    );
}

// ── Topic section ──────────────────────────────────────────────────────────

function TopicSection({ topic, lang }) {
    const { t } = useTranslation();
    const { key, Icon, visual, features } = topic;

    return (
        <section className="border-b border-border" id={`topic-${key}`}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
                {/* Section header */}
                <div className="flex items-center gap-3 mb-2">
                    <Icon className="size-5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                    <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight uppercase">
                        {t(`features.topics.${key}.title`)}
                    </h2>
                </div>
                <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
                    {t(`features.topics.${key}.intro`)}
                </p>

                {/* Visual */}
                <TopicVisual visual={visual} topicKey={key} lang={lang} />

                {/* Feature grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {features.map(({ key: fKey, Icon: FIcon }) => (
                        <FeatureCard key={fKey} topicKey={key} featureKey={fKey} Icon={FIcon} />
                    ))}
                </div>
            </div>
        </section>
    );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function FeaturesPage() {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language.startsWith('sv') ? 'sv' : 'en';

    const toggleLanguage = () => {
        const next = currentLang === 'sv' ? 'en' : 'sv';
        i18n.changeLanguage(next);
    };

    return (
        <div className="min-h-screen bg-background text-foreground flex flex-col">
            {/* ── Top Navigation ─────────────────────────────────── */}
            <header className="border-b border-border sticky top-0 z-40 bg-background">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    {/* Logo + back link */}
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={t('features.back_to_home')}
                        >
                            <ArrowLeft className="size-4" aria-hidden="true" />
                            <span className="hidden sm:inline">{t('features.back_to_home')}</span>
                        </Link>
                        <div className="flex items-center gap-3">
                            <img
                                src={LOGO_URL}
                                alt="Hisland"
                                className="h-7 w-auto"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <span className="font-heading font-bold tracking-widest text-sm uppercase">
                                HISLAND TIME
                            </span>
                        </div>
                    </div>

                    {/* Right nav */}
                    <nav className="flex items-center gap-4">
                        <button
                            onClick={toggleLanguage}
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wider"
                            aria-label={t('landing.nav.lang_toggle')}
                        >
                            {currentLang === 'sv' ? 'EN' : 'SV'}
                        </button>
                        <Link
                            to="/login"
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                            {t('landing.nav.login')}
                        </Link>
                    </nav>
                </div>
            </header>

            <main className="flex-1">
                {/* ── Page hero ──────────────────────────────────────── */}
                <section className="border-b border-border bg-card">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                            {t('features.page_title')}
                        </h1>
                        <p className="text-base text-muted-foreground max-w-2xl leading-relaxed">
                            {t('features.page_intro')}
                        </p>
                    </div>
                </section>

                {/* ── Topic sections ──────────────────────────────────── */}
                {TOPICS.map((topic) => (
                    <TopicSection key={topic.key} topic={topic} lang={currentLang} />
                ))}

                {/* ── Closing CTA ─────────────────────────────────────── */}
                <section className="border-b border-border bg-card">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 text-center">
                        <p className="text-lg font-semibold mb-6">
                            {t('features.closing_cta')}
                        </p>
                        <a href="/#contact">
                            <button
                                className="inline-flex items-center gap-2 px-8 py-3 bg-foreground text-background text-sm font-semibold uppercase tracking-widest rounded-md hover:opacity-90 transition-opacity"
                                type="button"
                            >
                                {t('features.closing_cta_link')}
                            </button>
                        </a>
                    </div>
                </section>
            </main>

            <PublicFooter />
        </div>
    );
}
